<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
interface Emoji {
  symbol: string
  x: number
  y: number
  collected: boolean
  rotation: number
}
const gameBoard = ref<HTMLElement | null>(null)
const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

const totalEmojis = isMobile.value ? 15 : 30
const emojiOptions = ['💩']
const emojis = ref<Emoji[]>([])
const collectedCount = ref<number>(0)

const emojiSize = 40
const collectionRadius = 40

const clampEmojiPositionsToContainer = useDebounceFn(() => {
  const container = gameBoard.value?.getBoundingClientRect()
  if (!container) return

  const maxX = container.width - emojiSize
  const maxY = container.height - emojiSize

  emojis.value.forEach((emoji) => {
    emoji.x = Math.min(Math.max(0, emoji.x), maxX)
    emoji.y = Math.min(Math.max(0, emoji.y), maxY)
  })
}, 100)

const generateEmojis = () => {
  const padding = 40
  const container = gameBoard.value?.getBoundingClientRect()
  const width = container?.width || window.innerWidth
  const height = container?.height || window.innerHeight
  const minDistance = emojiSize + 10 // Minimum distance between emojis

  const newEmojis: Emoji[] = []

  for (let i = 0; i < totalEmojis; i++) {
    let attempts = 0
    let validPosition = false
    let x = 0
    let y = 0

    while (!validPosition && attempts < 100) {
      x = Math.random() * (width - padding)
      y = Math.random() * (height - padding)

      validPosition = newEmojis.every((existingEmoji) => {
        const dx = x - existingEmoji.x
        const dy = y - existingEmoji.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        return distance > minDistance
      })

      attempts++
    }

    const rotation = Math.random() * 90 - 45

    newEmojis.push({
      symbol: emojiOptions[Math.floor(Math.random() * emojiOptions.length)],
      x,
      y,
      rotation,
      collected: false,
    })
  }

  emojis.value = newEmojis
}

let animationFrameId: number

const checkEmojiCollection = (clientX: number, clientY: number) => {
  const container = gameBoard.value?.getBoundingClientRect()
  if (!container) return

  // Convert to container-relative coordinates
  const x = clientX - container.left
  const y = clientY - container.top

  for (const emoji of emojis.value) {
    if (emoji.collected) continue

    const emojiCenterX = emoji.x + emojiSize / 2
    const emojiCenterY = emoji.y + emojiSize / 2
    const dx = x - emojiCenterX
    const dy = y - emojiCenterY
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < collectionRadius) {
      emoji.collected = true
      collectedCount.value++
    }
  }
}

const checkCollisions = () => {
  const container = gameBoard.value?.getBoundingClientRect()
  const cursor = document.querySelector('.mogbille-cursor') as HTMLElement
  if (!container || !cursor) return

  const rect = cursor.getBoundingClientRect()
  const cursorCenter = {
    x: rect.left + rect.width / 2 - container.left,
    y: rect.top + rect.height / 2 - container.top,
  }

  const radius = rect.width * 0.6

  for (const emoji of emojis.value) {
    if (!emoji.collected) {
      const dx = emoji.x - cursorCenter.x
      const dy = emoji.y - cursorCenter.y
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < radius) {
        emoji.collected = true
        collectedCount.value++
      }
    }
  }

  animationFrameId = requestAnimationFrame(checkCollisions)
}

const handleTouch = (e: TouchEvent) => {
  for (let i = 0; i < e.touches.length; i++) {
    const touch = e.touches[i]
    checkEmojiCollection(touch.clientX, touch.clientY)
  }
}

onMounted(() => {
  generateEmojis()
  window.addEventListener('resize', clampEmojiPositionsToContainer)
  window.addEventListener('touchstart', handleTouch)
  window.addEventListener('touchmove', handleTouch)
  animationFrameId = requestAnimationFrame(checkCollisions)
})

onUnmounted(() => {
  window.removeEventListener('resize', clampEmojiPositionsToContainer)
  window.removeEventListener('touchstart', handleTouch)
  window.removeEventListener('touchmove', handleTouch)
  cancelAnimationFrame(animationFrameId)
})

const allPoopsCollected = computed(() => {
  return collectedCount.value === totalEmojis
})

const confetti = ref<any>(null)
const { onLoaded } = useScriptNpm({
  packageName: 'js-confetti',
  file: 'dist/js-confetti.browser.js',
  version: '0.12.0',
  scriptOptions: {
    use() {
      return { JSConfetti: window.JSConfetti }
    },
  },
})

onMounted(() => {
  onLoaded(({ JSConfetti }) => {
    confetti.value = new JSConfetti()
  })
})

watch(allPoopsCollected, (value) => {
  if (value && confetti.value) {
    confetti.value.addConfetti({
      emojis: ['🌸', '🌳', '🍀', '🍂', '☀️'],
      emojiSize: 80,
      confettiNumber: isMobile.value ? 50 : 100,
    })
  }
})
</script>

<template>
  <div ref="gameBoard" class="relative h-full w-full overflow-hidden bg-(--sand) text-(--earth-1)">
    <div
      v-for="(emoji, index) in emojis"
      :key="index"
      class="pointer-events-none absolute text-3xl drop-shadow-[0px_8px_2px_rgba(0,0,0,0.05)] transition-opacity duration-300"
      :style="{
        left: `${emoji.x}px`,
        top: `${emoji.y}px`,
        transform: `rotate(${emoji.rotation}deg)`,
        opacity: emoji.collected ? '0' : '1',
        width: '40px',
        height: '40px',
        display: emoji.collected ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }"
    >
      {{ emoji.symbol }}
    </div>

    <div
      class="absolute right-4 bottom-4 rounded-full bg-(--theme-fg) px-4 py-2 text-lg font-semibold text-(--theme-bg) shadow-lg"
    >
      💩 Collected: {{ collectedCount }} / {{ emojis.length }}
    </div>
  </div>
</template>
